import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Copy, Pencil, Trash2, LayoutTemplate, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function QuotationTemplates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ["ADMIN", "SALES_MANAGER"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/quotation-templates");
    setItems(data); setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const duplicate = async (t) => {
    await api.post(`/quotation-templates/${t.id}/duplicate`);
    toast.success("Template duplicated"); load();
  };

  const doDelete = async () => {
    try { await api.delete(`/quotation-templates/${confirm.id}`); toast.success("Template deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    setConfirm(null); load();
  };

  if (!canManage) {
    return <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">Only Admins and Sales Managers can manage quotation templates.</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/quotations")} className="rounded-md p-1.5 hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Quotation Templates</h1>
          <p className="text-xs text-slate-500">Master templates reused across quotations. Editing a template does not change existing quotations.</p>
        </div>
        <Button onClick={() => setEdit({ name: "", description: "", content: {} })} data-testid="new-template-btn" className="gap-1.5"><Plus className="h-4 w-4" /> New Template</Button>
      </div>

      {loading ? <p>Loading…</p> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <div key={t.id} className="group rounded-xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#0057A8]"><LayoutTemplate className="h-5 w-5" /></div>
                {t.is_default && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">DEFAULT</span>}
                {t.is_system && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">SYSTEM</span>}
              </div>
              <h3 className="mt-3 text-base font-semibold text-slate-800">{t.name}</h3>
              <p className="mt-1 line-clamp-2 text-xs text-slate-500">{t.description || "—"}</p>
              <div className="mt-3 text-xs text-slate-400">Updated {formatDate(t.updated_at)}</div>
              <div className="mt-4 flex items-center gap-2 border-t border-slate-50 pt-3">
                <Button size="sm" variant="outline" onClick={() => duplicate(t)} className="gap-1 text-xs"><Copy className="h-3.5 w-3.5" /> Duplicate</Button>
                {!t.is_system && <Button size="sm" variant="outline" onClick={() => setEdit(t)} className="gap-1 text-xs"><Pencil className="h-3.5 w-3.5" /> Edit</Button>}
                {!t.is_system && !t.is_default && <button onClick={() => setConfirm(t)} className="ml-auto rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateDialog open={!!edit} template={edit} onClose={() => setEdit(null)} onSaved={load} />
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)}
        title="Delete template?" description="Existing quotations keep their saved content." confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}

// Compares each top-level field in `content` against the default template.
// Returns a human-readable error string naming the first bad field, or null.
function validateAgainstDefaults(content, defaults) {
  const typeOf = (v) => {
    if (v === null || v === undefined) return "null";
    if (Array.isArray(v)) return "array";
    return typeof v; // "object" | "string" | "number" | "boolean"
  };
  if (typeOf(content) !== "object") {
    return `Content must be a JSON object (got ${typeOf(content)}).`;
  }
  for (const [key, defVal] of Object.entries(defaults || {})) {
    if (!(key in content)) continue; // allow partial templates — missing keys fall back to default
    const expected = typeOf(defVal);
    const actual = typeOf(content[key]);
    if (expected !== actual) {
      // Special-case the specific crash we hit: bank_details/further_info as object instead of array.
      if (expected === "array" && actual === "object") {
        return `"${key}" must be an array of {label, value} rows (got object). Example: [{"label": "Bank Name", "value": "HDFC BANK"}]`;
      }
      return `"${key}" must be ${expected} (got ${actual}).`;
    }
    // One level deeper for arrays of objects (like further_info, bank_details, methodology_steps).
    if (expected === "array" && Array.isArray(defVal) && defVal.length && typeof defVal[0] === "object") {
      const sampleKeys = Object.keys(defVal[0]);
      for (let i = 0; i < content[key].length; i++) {
        const row = content[key][i];
        if (typeOf(row) !== "object") {
          return `"${key}[${i}]" must be an object with keys ${sampleKeys.join(", ")} (got ${typeOf(row)}).`;
        }
      }
    }
  }
  return null;
}

function TemplateDialog({ open, template, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", content: "{}" });

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || "",
        description: template.description || "",
        content: JSON.stringify(template.content || {}, null, 2),
      });
    }
  }, [template]);

  const save = async () => {
    let content;
    try { content = JSON.parse(form.content || "{}"); }
    catch { return toast.error("Content JSON is invalid"); }

    // Validate against the default template's structure so a bad field is
    // caught before it crashes the quotation editor (e.g. bank_details saved
    // as an object instead of an array of {label,value} rows).
    try {
      const { data } = await api.get("/quotation-templates/default/content");
      const defaults = data?.content || {};
      const err = validateAgainstDefaults(content, defaults);
      if (err) return toast.error(err);
    } catch (e) {
      // If the default template can't be fetched, don't block saves — just log.
      console.warn("Skipping template schema validation:", e?.message);
    }

    const payload = { name: form.name, description: form.description, content };
    try {
      if (template?.id) await api.put(`/quotation-templates/${template.id}`, payload);
      else await api.post("/quotation-templates", payload);
      toast.success(template?.id ? "Template saved" : "Template created");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{template?.id ? `Edit Template — ${template.name}` : "New Template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs font-semibold">Content (JSON — mirrors the quotation sections)</Label>
            <Textarea rows={16} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="font-mono text-xs" />
            <p className="mt-1 text-[11px] text-slate-500">Tip: duplicate the default template, then tweak this JSON (cover, intro_text, methodology_steps, terms, bank_details…).</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="save-template-btn">Save Template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
